import {
  Alert,
  EmptyState,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import type { JSX } from 'react';

import { listContracts, type ContractListItem } from '../../../contracts/api';
import { formatCalendarDate } from '../../../contracts/format-calendar-date';

export interface ContractVersionHistoryTabProps {
  code: string;
}

/**
 * `contracts/spec.md` → "Distinción entre versión superada y contrato
 * vencido". `apps/api` has no dedicated version-chain endpoint; `GET
 * /contracts` (`listContracts`) already returns every contract in scope
 * with `code`/`version`/`parentContractId`/`status`, enough to
 * reconstruct the chain and derive `supersededByVersion` client-side —
 * per this PR's own instruction to check the existing list response
 * before adding a new endpoint.
 */
export function ContractVersionHistoryTab({ code }: ContractVersionHistoryTabProps): JSX.Element {
  const query = useQuery({ queryKey: ['contracts'], queryFn: () => listContracts() });

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return <Alert tone="danger">No pudimos cargar el historial de versiones.</Alert>;
  }

  const chain = query.data
    .filter((contract) => contract.code === code)
    .sort((a, b) => a.version - b.version);

  if (chain.length === 0) {
    return <EmptyState icon={History} title="Todavía no hay versiones para este contrato." />;
  }

  return (
    <Table>
      <caption className="sr-only">Historial de versiones</caption>
      <thead>
        <tr>
          <TableHeader>Versión</TableHeader>
          <TableHeader>Estado</TableHeader>
          <TableHeader>Vigencia</TableHeader>
          <TableHeader>Motivo de cierre</TableHeader>
        </tr>
      </thead>
      <TableBody>
        {chain.map((contract) => (
          <TableRow key={contract.id}>
            <TableCell className="font-medium text-slate-900">v{contract.version}</TableCell>
            <TableCell>
              <StatusBadge domain="contract" status={contract.status} />
            </TableCell>
            <TableCell>{formatValidity(contract)}</TableCell>
            <TableCell>{closingReason(contract, chain)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function formatValidity(contract: ContractListItem): string {
  const start = formatCalendarDate(contract.startDate);

  if (contract.endDate === null) {
    return `Desde ${start}`;
  }

  return `${start} – ${formatCalendarDate(contract.endDate)}`;
}

/** `null` reason for anything not `EXPIRED`: the enmienda/vencimiento distinction only applies to a closed version (`contracts/spec.md`). */
function closingReason(contract: ContractListItem, chain: ContractListItem[]): string {
  if (contract.status !== 'EXPIRED') {
    return '—';
  }

  const successor = chain.find((candidate) => candidate.parentContractId === contract.id);

  return successor === undefined
    ? 'Vencido sin sucesor'
    : `Reemplazado por la versión ${successor.version}`;
}
